import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2Icon, CalendarIcon, HomeIcon, SearchIcon, PlusIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from "@/components/ui/button";
import { DailySamplesTable } from '@/features/loadsamples/DailySamplesTable';
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { format, subDays } from 'date-fns';
import MainLayout from '@/components/layout/MainLayout';
import { cn } from '@/lib/utils';
import SearchBar from '@/components/search/SearchBar';
import { SampleFilters } from '@/components/daily-samples/SampleFilters';
import { Link } from 'react-router-dom';
import { RoleBasedElement } from '@/features/auth/components/RoleBasedElement';
import { ROLES } from '@/features/auth/constants/roles';
import { useSampleTypes } from '@/features/samples/hooks/useSampleTypes';
import { useSampleSearch } from '@/features/samples/hooks/useSampleSearch';
import { useLocalities } from '@/features/alojamientos/hooks/useLocalities';
import { useToast } from '@/components/ui/use-toast';
import { useRoleAccess } from '@/features/auth/hooks/useRoleAccess';
import { Sample } from '@/features/samples/services/sampleSearchService';
import { useSampleUserTypes } from '@/features/samples/hooks/useSampleUserTypes';
import { useSamplingBlockReasons } from '@/features/samples/hooks/useSamplingBlockReasons';


const DailySamples = () => {
  const [searchParams] = useSearchParams();
  const { sampleTypes, isLoading: isLoadingSampleTypes } = useSampleTypes();
  const { searchSamples, samples: fetchedSamples, isLoading: isLoadingSamples, error } = useSampleSearch();
  const { localities } = useLocalities();
  const { toast } = useToast();
  const { hasRole } = useRoleAccess();

  // Initialize date and selectedSample from URL params if present
  const dateParam = searchParams.get('date');
  const sampleTypeParam = searchParams.get('sampleType');

  const [date, setDate] = useState<Date>(() => {
    if (dateParam) {
      try {
        const parsedDate = new Date(dateParam);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      } catch (error) {
        console.error('Error parsing date from URL:', error);
      }
    }
    return new Date();
  });

  const [selectedSample, setSelectedSample] = useState<string>(sampleTypeParam || "");
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('todas');
  const [selectedLocality, setSelectedLocality] = useState('todas');
  const [selectedSegment, setSelectedSegment] = useState('todos');
  const [localSamples, setLocalSamples] = useState<Sample[]>([]);
  const { searchSampleUserTypes, sampleUserTypes } = useSampleUserTypes();
  const { samplingBlockReasons, isLoading: isLoadingSamplingBlockReasons } = useSamplingBlockReasons();
  const [initialSearchDone, setInitialSearchDone] = useState(false);

  // Effect to perform initial search based on URL params or user role
  useEffect(() => {
    if (initialSearchDone || isLoadingSampleTypes) return;

    const performInitialSearch = () => {
      const canUseSampleType = hasRole([ROLES.ADMIN, ROLES.OBSERVATORIO_EDITOR]);
      const isObserver = hasRole([ROLES.OBSERVATORIO_OBSERVADOR]);

      // If we have URL params (dateParam or sampleTypeParam), use them
      if (dateParam || sampleTypeParam) {
        searchSamples({
          sampleDate: dateParam || format(date, 'yyyy-MM-dd'),
          sampleTypeId: canUseSampleType && sampleTypeParam ? parseInt(sampleTypeParam) : null
        }, {
          onError: () => {
            toast({
              title: "Error",
              description: "Error al cargar las muestras",
              variant: "destructive"
            });
          }
        });
        setInitialSearchDone(true);
        return;
      }

      // Otherwise, if user is observer, do default search
      if (isObserver) {
        searchSamples({
          sampleDate: format(date, 'yyyy-MM-dd'),
          sampleTypeId: null
        }, {
          onError: () => {
            toast({
              title: "Error",
              description: "Error al cargar las muestras iniciales",
              variant: "destructive"
            });
          }
        });
        setInitialSearchDone(true);
      }
    };

    performInitialSearch();
  }, [initialSearchDone, isLoadingSampleTypes, dateParam, sampleTypeParam, date, hasRole, searchSamples, toast]);

  // Effect to load sample user types
  useEffect(() => {
    const searchSampleParams = {
      dateFrom: date,
      dateTo: date,
    };

    searchSampleUserTypes(searchSampleParams);
  }, [date, searchSampleUserTypes]);

  // Establecer el primer tipo de muestra como valor por defecto (solo si no hay valor de URL)
  useEffect(() => {
    if (sampleTypes && sampleTypes.length > 0 && !selectedSample && !sampleTypeParam) {
      setSelectedSample(sampleTypes[0].id.toString());
    }
  }, [sampleTypes, selectedSample, sampleTypeParam]);

  // Actualizar localSamples cuando fetchedSamples cambie
  useEffect(() => {
    if (fetchedSamples) {
      setLocalSamples(fetchedSamples);
    }
  }, [fetchedSamples]);

  const handleSearch = async () => {
    if (!date) {
      toast({
        title: "Error",
        description: "Por favor selecciona una fecha",
        variant: "destructive"
      });
      return;
    }

    // Verificar si el usuario tiene los roles necesarios para usar sampleTypeId
    const canUseSampleType = hasRole([ROLES.ADMIN, ROLES.OBSERVATORIO_EDITOR]);

    // Buscar muestras
    await searchSamples({
      sampleDate: format(date, 'yyyy-MM-dd'),
      sampleTypeId: canUseSampleType && selectedSample ? parseInt(selectedSample) : null
    }, {
      onError: (error) => {
        toast({
          title: "Error",
          description: "Error al buscar muestras",
          variant: "destructive"
        });
      }
    });

  };


  return (
    <MainLayout defaultCollapsed={true}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/">
                    <HomeIcon className="h-4 w-4 mr-1" />
                    <span>Inicio</span>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Cargar muestras</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div>
              <h1 className="text-3xl font-bold text-gray-900 mt-6">Completar muestra</h1>
              <p className="text-muted-foreground mt-1">
              Carga diaria de ocupación hotelera
              </p>
            </div>
          </div>
        </div>

        <Card className="mb-6">
          <CardContent>
            <div className="flex flex-wrap gap-4 items-center mt-6">
             
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-[200px] justify-start text-left font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PP") : <span>Fecha</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(selectedDate) => selectedDate && setDate(selectedDate)}
                    initialFocus
                    captionLayout="dropdown"
                    fromYear={1950}
                    toYear={2030}
                    disabled={!hasRole([ROLES.ADMIN, ROLES.OBSERVATORIO_EDITOR])}
                  />
                </PopoverContent>
              </Popover>
              
              <RoleBasedElement requiredRoles={[ROLES.ADMIN, ROLES.OBSERVATORIO_EDITOR]}>
                <Select value={selectedSample} onValueChange={setSelectedSample}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Seleccionar muestra" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingSampleTypes ? (
                      <SelectItem value="loading" disabled>
                        Cargando tipos de muestra...
                      </SelectItem>
                    ) : sampleTypes && sampleTypes.length > 0 ? (
                      sampleTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-data" disabled>
                        No hay tipos de muestra disponibles
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <SampleFilters
                selectedRegion={selectedRegion}
                selectedLocality={selectedLocality}
                selectedSegment={selectedSegment}
                onRegionChange={setSelectedRegion}
                onLocalityChange={setSelectedLocality}
                onSegmentChange={setSelectedSegment}
                className="flex-row gap-4"
              />
              <Button 
                onClick={handleSearch} 
                className="shrink-0"
                disabled={isLoadingSamples}
              >
                <SearchIcon className="h-4 w-4 mr-2" />
                {isLoadingSamples ? 'Buscando...' : 'Buscar'}
              </Button>
              </RoleBasedElement>
              
              

              
              
            </div>
          </CardContent>
        </Card>

        <DailySamplesTable 
          samples={localSamples} 
          localities={localities}
          isLoading={isLoadingSamples}
          selectedSampleType={selectedSample ? parseInt(selectedSample) : null}
          sampleUserTypes={sampleUserTypes}
          samplingBlockReasons={samplingBlockReasons}
        />
      </div>
    </MainLayout>
  );
};

export default DailySamples;
